import { Check, LayoutDashboard, ShoppingBag, Sparkles, Users, Wallet, Workflow } from "lucide-react";
import { WhatsAppIcon } from "./BrandIcon";
import { useLoopedCount } from "@/hooks/useLoopedCount";
import { cn } from "@/lib/utils";

const SIDEBAR_ITEMS = [
  { icon: LayoutDashboard, label: "Painel", active: true },
  { icon: ShoppingBag, label: "Pedidos" },
  { icon: Users, label: "Clientes" },
  { icon: Wallet, label: "Financeiro" },
  { icon: Sparkles, label: "Assistente IA" },
];

const KPIS = [
  { label: "Pedidos", value: "128", note: "+12%", noteClass: "text-emerald-700" },
  { label: "Faturamento", value: "R$ 46,2k", note: "+8%", noteClass: "text-emerald-700" },
  { label: "Automações", value: "1.240", note: "tarefas hoje", noteClass: "text-brand-600" },
];

const CHART_BARS = [38, 52, 45, 60, 48, 72, 58, 80, 66, 90, 74, 96];

const ORDERS = [
  { id: "#4821", customer: "Mercado Bom Preço", status: "Faturado", statusClass: "bg-emerald-50 text-emerald-700" },
  { id: "#4820", customer: "Clínica Vida", status: "Em separação", statusClass: "bg-amber-50 text-amber-700" },
  { id: "#4819", customer: "Loja Central", status: "Aprovado", statusClass: "bg-brand-50 text-brand-700" },
];

const AUTOMATION_STEPS = ["Pedido aprovado", "Nota fiscal emitida", "Estoque atualizado", "Cliente avisado"];
const AI_QUESTION = "Quais clientes estão sem comprar há 60 dias?";
const AI_ANSWER = "Encontrei 37 clientes. Quer que eu prepare uma campanha de reativação?";

/** Ilustração do hero: painel de um sistema sob medida com automação, IA e agente de WhatsApp. */
const HeroCollage = () => {
  const stepsDone = useLoopedCount(AUTOMATION_STEPS.length, { start: 1200, step: 750, hold: 2800 });
  const typedLength = useLoopedCount(AI_ANSWER.length, { start: 2200, step: 26, hold: 4500 });
  const isTyping = typedLength < AI_ANSWER.length;

  return (
    <figure aria-hidden="true" className="relative mx-auto mb-6 w-full max-w-[540px] lg:mb-0 lg:ml-auto">
      <div className="absolute -inset-8 rounded-[48px] bg-gradient-to-b from-brand-500/20 to-transparent blur-2xl" />

      <div className="relative animate-float-in overflow-hidden rounded-[22px] border border-ink-900/10 bg-white shadow-[0_40px_80px_-30px_rgb(44_24_128/.45)]">
        <div className="flex items-center gap-1.5 border-b border-ink-900/[0.06] px-4 py-3">
          <span className="h-2.5 w-2.5 rounded-full bg-ink-200" />
          <span className="h-2.5 w-2.5 rounded-full bg-ink-200" />
          <span className="h-2.5 w-2.5 rounded-full bg-ink-200" />
          <div className="ml-3 min-w-0 flex-1 truncate rounded-md bg-mist px-3 py-1 font-mono text-[11px] text-ink-500">
            app.suaempresa.com.br/painel
          </div>
        </div>

        <div className="flex">
          <aside className="hidden w-[132px] shrink-0 border-r border-ink-900/[0.06] bg-paper p-3 sm:block">
            <div className="flex items-center gap-2 px-1.5 text-[12px] font-semibold text-ink-900">
              <span className="grid h-5 w-5 place-items-center rounded-md bg-ink-900 text-[10px] text-white">S</span>
              Sua Empresa
            </div>
            <div className="mt-4 space-y-0.5 text-[12px] text-ink-500">
              {SIDEBAR_ITEMS.map(({ icon: Icon, label, active }) => (
                <div
                  key={label}
                  className={cn("flex items-center gap-2 rounded-lg px-2 py-1.5", active && "bg-brand-50 font-medium text-brand-700")}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </div>
              ))}
            </div>
          </aside>

          <div className="min-w-0 flex-1 p-4">
            <div className="flex items-center justify-between">
              <p className="text-[13px] font-semibold text-ink-900">Visão geral</p>
              <span className="rounded-full bg-mist px-2 py-0.5 font-mono text-[10px] text-ink-500">Hoje</span>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-2">
              {KPIS.map((kpi) => (
                <div key={kpi.label} className="rounded-xl border border-ink-900/[0.06] p-2.5">
                  <p className="text-[10px] text-ink-500">{kpi.label}</p>
                  <p className="mt-1 text-[16px] font-semibold tracking-tight text-ink-900">{kpi.value}</p>
                  <p className={cn("text-[10px] font-medium", kpi.noteClass)}>{kpi.note}</p>
                </div>
              ))}
            </div>

            <div className="mt-3 rounded-xl border border-ink-900/[0.06] p-3">
              <div className="flex h-16 items-end gap-1.5">
                {CHART_BARS.map((height, index) => (
                  <span
                    key={index}
                    className={cn("flex-1 rounded-t-[4px]", index === CHART_BARS.length - 1 ? "bg-brand-600" : "bg-brand-200")}
                    style={{ height: `${height}%` }}
                  />
                ))}
              </div>
            </div>

            <div className="mt-3 space-y-1.5 text-[11.5px]">
              {ORDERS.map((order) => (
                <div key={order.id} className="flex items-center justify-between gap-2 rounded-lg bg-paper px-2.5 py-1.5">
                  <span className="font-mono text-ink-500">{order.id}</span>
                  <span className="flex-1 truncate text-ink-700">{order.customer}</span>
                  <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-medium", order.statusClass)}>{order.status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Agente de IA no WhatsApp */}
      <div className="absolute -top-5 right-4 animate-float-in sm:right-8" style={{ animationDelay: "900ms" }}>
        <div className="flex animate-bob-slow items-center gap-2 rounded-full border border-ink-900/10 bg-white py-1.5 pl-1.5 pr-3.5 shadow-[0_12px_30px_-12px_rgb(25_25_25/.35)]">
          <span className="grid h-7 w-7 place-items-center rounded-full bg-whatsapp text-white">
            <WhatsAppIcon className="h-3.5 w-3.5" />
          </span>
          <span className="text-[12px] font-medium text-ink-800">Agente de IA qualificou um lead</span>
        </div>
      </div>

      {/* Automação */}
      <div
        className="absolute -left-3 top-[44%] hidden w-[214px] animate-float-in sm:-left-14 sm:block xl:-left-8"
        style={{ animationDelay: "500ms" }}
      >
        <div className="animate-bob rounded-2xl border border-ink-900/10 bg-white p-3.5 shadow-[0_20px_40px_-18px_rgb(25_25_25/.4)]">
          <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-brand-700">
            <Workflow className="h-3 w-3" />
            Automação
          </p>
          <ul className="mt-2.5 space-y-1.5 text-[12px]">
            {AUTOMATION_STEPS.map((step, index) => {
              const done = index < stepsDone;
              return (
                <li key={step} className={cn("flex items-center gap-2 transition-colors", done ? "text-ink-900" : "text-ink-500")}>
                  <span
                    className={cn(
                      "grid h-4 w-4 shrink-0 place-items-center rounded-full border transition-colors duration-300",
                      done ? "border-brand-600 bg-brand-600" : "border-ink-200 bg-white",
                    )}
                  >
                    <Check className={cn("h-2.5 w-2.5 text-white transition-opacity", done ? "opacity-100" : "opacity-0")} strokeWidth={3} />
                  </span>
                  {step}
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Assistente de IA */}
      <div
        className="absolute -bottom-12 -right-2 w-[248px] animate-float-in sm:-right-6 xl:-right-10"
        style={{ animationDelay: "1300ms" }}
      >
        <div
          className="animate-bob rounded-2xl bg-night p-3.5 text-white shadow-[0_24px_50px_-18px_rgb(26_14_79/.7)]"
          style={{ animationDelay: "-3s" }}
        >
          <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-brand-300">
            <Sparkles className="h-3 w-3" />
            Assistente de IA
          </p>
          <p className="mt-2.5 rounded-lg bg-white/10 px-2.5 py-1.5 text-[12px] leading-snug text-white/85">{AI_QUESTION}</p>
          <p className="mt-2 min-h-[34px] text-[12px] leading-snug text-white">
            {AI_ANSWER.slice(0, typedLength)}
            {isTyping && <span className="ml-0.5 inline-block h-3 w-1.5 animate-caret bg-current align-[-1px]" />}
          </p>
        </div>
      </div>
    </figure>
  );
};

export default HeroCollage;
